import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, CheckCircle2, XCircle, Loader2, UserX, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { formatDateWithWeekday, formatTimeRangeUhr } from '@/lib/utils';

const API_BASE = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/v1`;

/**
 * Eltern-Magic-Link (Phase F7).
 * URL: /parent-slot/:token
 *
 * Zwei Haupt-Szenarien (backend liefert `teacher_status`):
 *  1) `proposed_alternatives` → Liste vorgeschlagener Slots, Eltern wählen einen
 *  2) `declined` → Lehrkraft hat abgesagt, alternative Lehrkraft vorgeschlagen, Eltern akzeptieren/ablehnen
 *
 * In beiden Fällen 3 Optionen:
 *  a) annehmen  b) andere Lehrkraft vorschlagen  c) ablehnen
 */
export default function ParentSlotMagicLinkPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [chosenSlotIndex, setChosenSlotIndex] = useState(null);
  const [otherMode, setOtherMode] = useState(false);
  const [declineMode, setDeclineMode] = useState(false);
  const [reasonText, setReasonText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_BASE}/public/registrations/parent-slot/${encodeURIComponent(token)}`,
      );
      setInfo(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Link nicht gültig oder abgelaufen.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    if (info.teacher_status === 'proposed_alternatives' && chosenSlotIndex == null) {
      toast.error('Bitte wählen Sie einen Termin');
      return;
    }
    setSubmitting(true);
    try {
      const body = info.teacher_status === 'proposed_alternatives'
        ? { slot_index: chosenSlotIndex }
        : {};
      const r = await axios.post(
        `${API_BASE}/public/registrations/parent-slot/${encodeURIComponent(token)}/accept`,
        body,
      );
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  const requestOther = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API_BASE}/public/registrations/parent-slot/${encodeURIComponent(token)}/request-other-teacher`,
        { reason: reasonText.trim() || null },
      );
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally { setSubmitting(false); }
  };

  const decline = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API_BASE}/public/registrations/parent-slot/${encodeURIComponent(token)}/decline`,
        { reason: reasonText.trim() || null },
      );
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally { setSubmitting(false); }
  };

  const fmtDate = (iso) => formatDateWithWeekday(iso) || iso;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50/40 py-10 px-4">
      <div className="max-w-2xl mx-auto" data-testid="parent-slot-magic-page">
        {loading && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-10 flex items-center justify-center gap-3 text-slate-500" data-testid="parent-slot-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Lade Auswahl …
          </div>
        )}

        {!loading && error && (
          <div className="bg-white rounded-2xl shadow-sm border border-rose-200 p-8" data-testid="parent-slot-error">
            <div className="flex items-center gap-2 text-rose-700 mb-2">
              <XCircle className="h-5 w-5" /> <span className="font-semibold">Link ungültig</span>
            </div>
            <p className="text-sm text-slate-600">{error}</p>
          </div>
        )}

        {!loading && !error && result && (
          <div className="bg-white rounded-2xl shadow-sm border border-emerald-300 p-8" data-testid="parent-slot-result">
            <div className="flex items-center gap-2 text-emerald-700 mb-3">
              <CheckCircle2 className="h-6 w-6" />
              <span className="text-lg font-semibold">Danke für Ihre Rückmeldung!</span>
            </div>
            <p className="text-slate-700">{result.message}</p>
          </div>
        )}

        {!loading && !error && !result && info && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
            <div className="mb-6">
              <div className="text-xs uppercase tracking-wider text-brand-red font-medium mb-1">{info.tenant_name}</div>
              <h1 className="text-2xl font-semibold text-slate-900">
                {info.teacher_status === 'proposed_alternatives'
                  ? 'Alternativtermine vorgeschlagen'
                  : 'Lehrkraft nicht verfügbar'}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Für <strong>{info.student_name}</strong> · {info.instrument}
              </p>
            </div>

            {info.teacher_status === 'proposed_alternatives' && (
              <div className="mb-5">
                <p className="text-sm text-slate-700 mb-3">
                  <strong>{info.teacher_name}</strong> kann den ursprünglichen Termin nicht anbieten und
                  schlägt folgende Alternativen vor. Bitte wählen Sie einen:
                </p>
                <div className="space-y-2" data-testid="parent-slot-alternatives">
                  {info.alternatives.map((s, i) => {
                    const isChosen = chosenSlotIndex === i;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setChosenSlotIndex(i)}
                        className={`w-full text-left border rounded-xl p-4 transition ${
                          isChosen ? 'border-brand-red bg-brand-red/5 ring-2 ring-brand-red'
                                   : 'border-slate-200 hover:border-brand-red/40 hover:bg-brand-red/5'
                        }`}
                        data-testid={`parent-slot-alt-${i}`}
                      >
                        <div className="flex items-center gap-2 text-slate-900 font-medium">
                          <Calendar className="h-4 w-4 text-brand-red" /> {fmtDate(s.date)}
                        </div>
                        <div className="mt-1 text-sm text-slate-700">
                          {formatTimeRangeUhr(s.start_time, s.end_time)} · {s.duration_minutes || 45} Min
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {info.teacher_status === 'declined' && (
              <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-900">
                  <strong>{info.teacher_name}</strong> hat leider keine freien Kapazitäten. Wir schlagen
                  vor, Ihren Unterricht bei <strong>{info.alt_teacher_name || 'einer anderen Lehrkraft'}</strong> durchzuführen.
                </p>
              </div>
            )}

            {!otherMode && !declineMode && (
              <div className="flex flex-col sm:flex-row gap-3" data-testid="parent-slot-actions">
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-12 text-base font-semibold"
                  onClick={accept}
                  disabled={submitting || (info.teacher_status === 'proposed_alternatives' && chosenSlotIndex == null)}
                  data-testid="parent-slot-accept-btn"
                >
                  {submitting ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CheckCircle2 className="h-5 w-5 mr-2" />}
                  {info.teacher_status === 'proposed_alternatives' ? 'Gewählten Termin bestätigen' : 'Alt-Lehrkraft annehmen'}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 h-12 text-base border-amber-300 text-amber-700 hover:bg-brand-red/5"
                  onClick={() => setOtherMode(true)}
                  disabled={submitting}
                  data-testid="parent-slot-other-btn"
                >
                  <UserPlus className="h-5 w-5 mr-2" /> Andere Lehrkraft
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 h-12 text-base"
                  onClick={() => setDeclineMode(true)}
                  disabled={submitting}
                  data-testid="parent-slot-decline-btn"
                >
                  <UserX className="h-5 w-5 mr-2" /> Ablehnen
                </Button>
              </div>
            )}

            {(otherMode || declineMode) && (
              <div className="space-y-3" data-testid="parent-slot-reason-form">
                <label className="text-sm font-medium text-slate-700">
                  {otherMode ? 'Welche Art Lehrkraft wünschen Sie? (optional)' : 'Grund für Ablehnung (optional)'}
                </label>
                <Textarea
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  placeholder={otherMode
                    ? 'z. B. Lieber eine erfahrenere Lehrkraft, oder ...'
                    : 'z. B. Kurs wird nicht mehr benötigt'}
                  rows={3}
                  maxLength={500}
                  data-testid="parent-slot-reason-text"
                />
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => { setOtherMode(false); setDeclineMode(false); setReasonText(''); }}
                    disabled={submitting}
                    data-testid="parent-slot-reason-cancel"
                  >
                    Zurück
                  </Button>
                  <Button
                    className={`flex-1 ${otherMode ? 'bg-brand-red hover:bg-brand-red/90' : 'bg-rose-600 hover:bg-rose-700'}`}
                    onClick={otherMode ? requestOther : decline}
                    disabled={submitting}
                    data-testid="parent-slot-reason-submit"
                  >
                    {submitting ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : null}
                    {otherMode ? 'Andere Lehrkraft anfragen' : 'Ablehnung absenden'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
